import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ICardInterface } from 'src/app/interface/ICardResponse.interface';
import { ICardSettings } from 'src/app/interface/ICardSettings.interface';
import { Card } from 'src/app/model/card.model';
import { User } from 'src/app/model/user.model';
import { AppSettings } from 'src/app/services/app-settiings/app-settings.service';
import { CardSettingsService } from 'src/app/services/card-settings/card-settings.service';
import { BottomSheetService } from 'src/app/services/bottom-sheet/bottom-sheet.service';
import { GtmConsolidatedDiagnosticComponent } from '../gtm-consolidated-diagnostic/gtm-consolidated-diagnostic.component';
import { HistoricalBottomSheetComponent } from '../historical-bottom-sheet/historical-bottom-sheet.component';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  innerWidth = window.innerWidth;
  innerHeight = window.innerHeight;
  @ViewChild('headerCanvas', { static: false }) canvasRef: ElementRef<HTMLCanvasElement>;
  menuOpen = false;
  private canvas: HTMLCanvasElement;
  private stage: any;
  private exportRoot: any;
  private fnStartAnimation: () => void;
  private cardSettingsSubscription: Subscription;
  isChangingStatus: boolean = true;
  cardInformation!: ICardInterface;
  cardSettings: ICardSettings;
  isProductionMode = environment.production;

  constructor(
    private route: Router,
    public appSettings: AppSettings,
    private cardSettingsService: CardSettingsService,
    private dialog: MatDialog,
    private bottomSheetService: BottomSheetService
  ) {
    this.cardInformation = JSON.parse(localStorage.getItem('cardInformation'));
    if (this.cardInformation && this.cardInformation.card && this.cardInformation.card.accountNumber) {
      this.checkSettingsAndShowDialogs();
    } else {
      // Sin cache/datos de tarjeta: no bloquear la UI
      this.isChangingStatus = false;
    }

    // Suscribirse a cambios en la configuración de ofertas desde otros componentes
    this.cardSettingsSubscription = this.cardSettingsService.cardSettingsChanged$.subscribe((updatedSettings) => {
      // Solo actualizar si es para la misma cuenta
      if (this.cardSettings && updatedSettings && this.cardSettings.account === updatedSettings.account) {
        this.cardSettings = updatedSettings;
      }
    });
  }

  ngOnInit(): void {
    this.loadScript('https://code.createjs.com/1.0.0/createjs.min.js', () => {
      this.loadScript('assets/js/logo-happy-plus.js', () => {
        this.init();
      });
    });
  }

  ngOnDestroy(): void {
    // Limpiar suscripción para evitar memory leaks
    if (this.cardSettingsSubscription) {
      this.cardSettingsSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.canvasRef && this.canvasRef.nativeElement instanceof HTMLCanvasElement) {
      this.canvas = this.canvasRef.nativeElement;
    } else {
      console.error('Error: canvasRef.nativeElement no es un HTMLCanvasElement.');
    }

    this.init();
  }

  loadScript(url: string, callback?: () => void) {
    const script = document.createElement('script');
    script.src = url;
    script.onload = callback || (() => {});
    document.body.appendChild(script);
  }

  init(): void {
    if (!this.canvas) {
      console.error('Elementos no encontrados en el DOM. Verifica los IDs de los elementos.');
      return;
    }

    const comp = (window as any).AdobeAn.getComposition('00F0797A25BA4375BEC7C4ED9EEFA2E2');
    const lib = comp.getLibrary();
    const loader = new createjs.LoadQueue(false);

    loader.addEventListener('fileload', (evt: any) => this.handleFileLoad(evt, comp));
    loader.addEventListener('complete', (evt: any) => this.handleComplete(evt, comp));

    loader.loadManifest(lib.properties.manifest);
  }

  handleFileLoad(evt: any, comp: any): void {
    const images = comp.getImages();
    if (evt && evt.item.type === 'image') {
      images[evt.item.id] = evt.result;
    }
  }

  handleComplete(evt: any, comp: any): void {
    const lib = comp.getLibrary();
    const ss = comp.getSpriteSheet();
    const queue = evt.target;
    const ssMetadata = lib.ssMetadata;

    for (let i = 0; i < ssMetadata.length; i++) {
      ss[ssMetadata[i].name] = new createjs.SpriteSheet({
        images: [queue.getResult(ssMetadata[i].name)],
        frames: ssMetadata[i].frames,
      });
    }

    this.exportRoot = new lib.logoH();

    // Asigna 'stage' al objeto 'window' para que sea accesible globalmente
    (window as any).stage = this.stage = new lib.Stage(this.canvas);

    this.fnStartAnimation = () => {
      this.stage.addChild(this.exportRoot);
      createjs.Ticker.framerate = lib.properties.fps;
      createjs.Ticker.addEventListener('tick', this.stage);
    };

    (window as any).AdobeAn.makeResponsive(false, 'both', false, 1, [this.canvas]);
    (window as any).AdobeAn.compositionLoaded(lib.properties.id);
    this.fnStartAnimation();
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  goToPortal(): void {
    // Cerrar cualquier bottom sheet abierto
    if (this.bottomSheetService.isOpen()) {
      this.bottomSheetService.close();
    }

    const cardCodeCache: string | null = localStorage.getItem('card-code');
    const userCacheData: string | null = localStorage.getItem('cardInformation');

    if (cardCodeCache !== null) {
      this.route.navigate([`/portal/${cardCodeCache}`]);
    } else if (userCacheData !== null) {
      const lastCacheUser: { card: Card; enroll: User } = JSON.parse(userCacheData);

      this.route.navigate([`/portal/${lastCacheUser.card.accountNumber}`]);
    } else {
      this.route.navigate([`/portal/`]);
    }

    if (this.menuOpen) this.toggleMenu();
  }

  changePage(src: string): void {
    // Cerrar cualquier bottom sheet abierto
    if (this.bottomSheetService.isOpen()) {
      this.bottomSheetService.close();
    }

    // Normalise the target path to start with '/'
    const targetPath = src.startsWith('/') ? src : `/${src}`;
    // Strip query parameters from the current URL for a clean comparison
    const currentPath = this.route.url.split('?')[0];

    if (currentPath === targetPath) {
      /**
       * If we're already on the requested route, force a full refresh by
       * briefly navigating away (without changing the visible URL) and then back.
       * skipLocationChange keeps the browser history clean.
       */
      this.route.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.route.navigate([src]);
      });
    } else {
      // Navigate normally if it's a different route
      this.route.navigate([src]);
    }

    if (this.menuOpen) {
      this.toggleMenu();
    }
  }

  changeToHistoricalPage(src: string, type: string): void {
    // Abrir el nuevo historical bottom sheet
    this.bottomSheetService.open(HistoricalBottomSheetComponent, { type });

    if (this.menuOpen) this.toggleMenu();
  }

  openWaiver(): void {
    // Cerrar cualquier bottom sheet abierto
    if (this.bottomSheetService.isOpen()) {
      this.bottomSheetService.close();
    }

    // Navegar a la página de waiver como navegación normal
    this.route.navigate(['/waiver']);

    if (this.menuOpen) this.toggleMenu();
  }

  private timeoutAfter<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return Promise.race([promise, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`Timeout ${label} (${ms}ms)`)), ms))]) as Promise<T>;
  }

  async checkSettingsAndShowDialogs() {
    const accountNumber = this.cardInformation?.card?.accountNumber;
    if (!accountNumber) {
      this.isChangingStatus = false;
      return;
    }

    const accountId = Number(accountNumber);

    try {
      // Usar caché primero para habilitar UI inmediatamente
      const cached = this.cardSettingsService.getCachedCardSettings(accountId);
      if (cached) {
        this.cardSettings = cached;
      }

      // Intentar usar cardSettings que vienen del balance
      if (this.cardInformation.cardSettings) {
        this.cardSettings = this.cardInformation.cardSettings;
        this.cardSettingsService.setCachedCardSettings(this.cardSettings);
      } else {
        // Fallback: obtener desde REST API si no viene en cardInformation
        this.cardSettings = await this.timeoutAfter(this.cardSettingsService.getLatestCardSettings(accountId), 8000, 'getLatestCardSettings');
      }

      if (this.cardInformation.enroll != null && this.cardSettings === null) {
        // CASO 1: Usuario sin configuración previa
        const settings: ICardSettings = {
          account: accountId,
          showSpecialOffer: true,
        };

        this.cardSettings = await this.timeoutAfter(this.cardSettingsService.createNewSettings(settings), 8000, 'createNewSettings');
      }
    } catch (error) {
      if (!this.cardSettings) {
        // Fallback seguro para no bloquear la UI
        this.cardSettings = { account: accountId, showSpecialOffer: true } as ICardSettings;
      }
    } finally {
      this.isChangingStatus = false;
    }
  }

  async saveUserSettings() {
    try {
      this.isChangingStatus = true;

      // Si no hay settings o falta account, no intentar persistir
      if (!this.cardSettings || !this.cardSettings.account) {
        this.isChangingStatus = false;
        return;
      }

      const previous = this.cardSettings.showSpecialOffer;
      const newValue = !previous;

      // Actualizar UI inmediatamente para feedback visual
      this.cardSettings.showSpecialOffer = newValue;

      // Persistir en REST API
      await this.timeoutAfter(this.cardSettingsService.updateCardSettingsREST(this.cardSettings.account, newValue), 8000, 'updateCardSettingsREST');
    } catch (errCritical) {
      // Revertir el cambio local si fallo persistente
      if (this.cardSettings) {
        this.cardSettings.showSpecialOffer = !this.cardSettings.showSpecialOffer;
      }
    } finally {
      this.isChangingStatus = false;
    }
  }

  /**
   * Abre el modal de diagnóstico GTM consolidado
   */
  openGtmDiagnostic(): void {
    if (!this.isProductionMode) {
      const dialogRef = this.dialog.open(GtmConsolidatedDiagnosticComponent, {
        width: '700px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: false,
        autoFocus: false,
        data: {},
      });

      dialogRef.afterClosed().subscribe((result) => {
        console.log('GTM Diagnostic modal closed');
      });
    }
  }
}
